<template>
  <div>
    <ConfigListItem
      label="Velocidade"
      :value="strValue"
      :date="maxUpdatedAt"
      :login="maxUpdatedBy"
      @click="openEditDialog"
    />

    <ODialog ref="dialog" :max-width="800">
      <v-card>
        <ODialogBar title="Origem da Velocidade" @close="dismiss" />
        <v-card-text v-if="readonly" class="pa-6">
          <p>
            O valor da configuração "Uso da Serial" está 0x03 ou 0x04, o que
            impede a alteração desta configuração.
          </p>
          <p>
            Entre em contato com o time de desenvolvimento para corrigir.
          </p>
        </v-card-text>
        <v-card-text v-else class="pa-6">
          <div class="d-flex align-start">
            <OVerticalTabs
              class="mr-6"
              v-model="clonedValues.speedSource"
              :tabs="[
                { value: 'sensor', text: 'Sensor' },
                { value: 'can', text: 'CAN' }
              ]"
            />
            <v-tabs-items
              class="flex-grow-1"
              v-model="clonedValues.speedSource"
            >
              <div class="px-4" v-if="clonedValues.speedSource == null">
                Escolha um valor da lista ao lado.
              </div>

              <v-tab-item value="sensor" class="px-4 pb-1">
                <OTextField
                  v-model.number="clonedValues.speedConstant"
                  label="Valor da constante"
                />
              </v-tab-item>

              <v-tab-item value="can" class="px-4">
                <SelectCANVariable
                  :canDictId="canDictId"
                  v-model="clonedValues.speedCanVariable"
                />
              </v-tab-item>
            </v-tabs-items>
          </div>

          <div class="d-flex align-end gap-8 my-10 px-6">
            <OTextField
              class="w-150"
              label="Min. Vel. GPS"
              v-model.number="clonedValues['velocidade-por-gps-min-speed']"
              suffix="km/h"
            />

            <OTextField
              class="w-150"
              label="Min. Tempo"
              v-model.number="clonedValues['velocidade-por-gps-min-time']"
              suffix="seg"
            />

            <v-dialog :max-width="400">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-notebook</v-icon>
                </v-btn>
              </template>
              <v-card>
                <v-card-text class="pa-6">
                  <p>
                    Se <b>NÃO</b> houver velocidade pelo sensor, <b>MAS</b> as
                    duas restrições estiverem satisfeitas (mínimo de X km/h em Y
                    seg), <b>ENTÃO</b> a velocidade passa a ser medida por GPS.
                  </p>
                  <p>
                    Isto permanece até que ela se torne 0. Depois, a regra
                    precisa ser aplicada novamente.
                  </p>
                  <p>Default 20 (0x14)</p>
                </v-card-text>
              </v-card>
            </v-dialog>
          </div>

          <div class="d-flex align-end gap-8 px-6">
            <OTextField
              class="w-150"
              label="Limite Delta-V"
              v-model.number="clonedValues['limite-delta-v']"
              suffix="km/h"
              hint="Máximo de deltaV que será considerado. Default 0 (sem limite)"
            />

            <OSelect
              class="w-250"
              label="Velocidade sem RPM pode abrir trecho"
              v-model="clonedValues['trecho-abre-apenas-com-rpm']"
              :items="trechoAbreApenasComRPMOpts"
              item-text="label"
            />

            <v-dialog :max-width="400">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-notebook</v-icon>
                </v-btn>
              </template>
              <v-card>
                <v-card-text class="pa-6">
                  <p>
                    Independente da origem da velocidade (mesmo que por GPS),
                    esta configuração afeta se ela abre ou não trecho.
                  </p>
                </v-card-text>
              </v-card>
            </v-dialog>
          </div>
        </v-card-text>
        <ODialogButtons
          v-if="!readonly"
          class="pa-6"
          txtApply="Salvar"
          @cancel="dismiss"
          @apply="save"
        />
      </v-card>
    </ODialog>
  </div>
</template>

<script>
import ConfigListItem from './ConfigListItem.vue'
import {
  mostRecentlyUpdated,
  decHex,
  getCfg,
  getManyCfgs
} from './config-utils.js'
import VehicleCfgs from 'vehicle-cfgs'
import OVerticalTabs from './OVerticalTabs.vue'
import SelectCANVariable from './SelectCANVariable.vue'

// usado aqui e na tela de cópia de configs
export function displayValue(allConfigs) {
  let cfgs = getManyCfgs(
    allConfigs,
    'speedConstant',
    'speedSource',
    'speedCanVariable',
    'velocidade-por-gps-min-speed',
    'velocidade-por-gps-min-time',
    'limite-delta-v',
    'trecho-abre-apenas-com-rpm'
  )

  switch (cfgs.speedSource) {
    case 'sensor': {
      let x = cfgs.speedConstant
      return `Sensor speedConstant=${decHex(x)}`
    }
    case 'can': {
      let y = cfgs.speedCanVariable
      return `CAN variable=${y && y.code}`
    }
    default:
      return '???'
  }
}

export function copyValue(allConfigs) {
  return getManyCfgs(
    allConfigs,
    'speedConstant',
    'speedSource',
    'speedCanVariable',
    'velocidade-por-gps-min-speed',
    'velocidade-por-gps-min-time',
    'limite-delta-v',
    'trecho-abre-apenas-com-rpm'
  )
}

export default {
  components: { ConfigListItem, OVerticalTabs, SelectCANVariable },
  props: {
    allConfigs: { required: true }
  },
  data() {
    return {
      clonedValues: {},

      trechoAbreApenasComRPMOpts: VehicleCfgs.get(
        'trecho-abre-apenas-com-rpm',
        'opts'
      )
    }
  },
  computed: {
    readonly() {
      let x = getCfg(this.allConfigs, 'uso-da-serial')
      return x == '0x03' || x == '0x04'
    },
    canDictId() {
      return getCfg(this.allConfigs, 'canDictId')
    },
    strValue() {
      return displayValue(this.allConfigs)
    },
    mostRecent() {
      return mostRecentlyUpdated(
        this.allConfigs,
        'speedConstant',
        'speedSource',
        'speedCanVariable',
        'velocidade-por-gps-min-speed',
        'velocidade-por-gps-min-time',
        'limite-delta-v',
        'trecho-abre-apenas-com-rpm'
      )
    },
    maxUpdatedAt() {
      return this.mostRecent && this.mostRecent.updatedAt
    },
    maxUpdatedBy() {
      return this.mostRecent && this.mostRecent.updatedBy
    }
  },
  methods: {
    dismiss() {
      this.$refs.dialog.$dismiss()
    },
    openEditDialog() {
      this.clonedValues = getManyCfgs(
        this.allConfigs,
        'speedConstant',
        'speedSource',
        'speedCanVariable',
        'velocidade-por-gps-min-speed',
        'velocidade-por-gps-min-time',
        'limite-delta-v',
        'trecho-abre-apenas-com-rpm'
      )
      return this.$refs.dialog.$open()
    },
    async save() {
      const payload = this.clonedValues
      this.$emit('save', payload)
      this.dismiss()
    }
  }
}
</script>

<style></style>
